import { renderProviderSchedulePage } from 'pages/participation/renderProviderSchedulePage';
import { ssoLoginWithToken, consumeMagicLink } from 'services/authentication/authApi';
import { renderPolicyCatalogPage } from 'pages/policies/renderPolicyCatalogPage';
import { renderTemplatesPage } from 'pages/templates/renderTemplatesPage';
import { registrationModal } from 'components/modals/registrationModal';
import { ensureConnected, queueKey } from 'services/messaging/socketIo';
import { resetActivityTimer } from 'services/staleness/stalenessGuard';
import { renderSettingsPage } from 'pages/settings/renderSettingsPage';
import { renderPoliciesPage } from 'pages/policies/renderPoliciesPage';
import { displayTournament } from 'pages/tournament/tournamentDisplay';
import { logIn, logOut } from 'services/authentication/loginState';
import { tmxTournaments } from 'pages/tournaments/tournaments';
import { showSplash } from 'services/transitions/screenSlaver';
import { renderAdminPage } from 'pages/admin/renderAdminPage';
import { destroyTables } from 'pages/tournament/destroyTable';
import { firstPermittedTab, isTabAvailable, tabDenialReason } from 'services/capability/navCapability';
import { tmxToast } from 'services/notifications/tmxToast';
import { context } from 'services/context';
import Navigo from 'navigo';
import { t } from 'i18n';
import {
  forceExitAssignmentMode,
  isAssignmentMode,
} from 'pages/tournament/tabs/eventsTab/renderDraws/participantAssignmentMode';

import {
  DRAW,
  DRAW_ENTRIES,
  EVENT,
  TMX_TOURNAMENTS,
  PARTICIPANTS,
  SPLASH,
  STRUCTURE,
  TOURNAMENT,
  EVENTS_TAB,
  REPORTS_TAB,
  SCHEDULE2_TAB,
  SCHEDULING_TAB,
  VENUES_TAB,
  VENUE,
  TEMPLATES,
  POLICIES,
  PROVIDER,
  SCHEDULE,
  SETTINGS,
  INVITE,
  VIEW,
} from 'constants/tmxConstants';

export function routeTMX() {
  const routerRoot = '/';

  const useHash = true;
  const router = new Navigo(useHash ? '/' : `/${routerRoot}`, { hash: useHash });

  // make accessible
  context.router = router;

  // Reconnect socket on any navigation if it was lost
  router.hooks({
    before(done) {
      if (isAssignmentMode()) forceExitAssignmentMode();
      ensureConnected();
      resetActivityTimer();
      done();
    },
  });

  const displayRoute = ({ selectedTab, renderDraw, renderPoints, data }: any) => {
    // Route-level guard. Hiding a nav icon is not enough on its own: a bookmark,
    // a shared link, browser history or a typed hash all reach the route
    // directly. Guarding here rather than at each `router.on` puts it in the one
    // function every tab-selecting route already calls (standard A10).
    //
    // `isTabAvailable`, not `canViewTab`: a tab switched off in Settings must
    // stop answering bookmarks too, or the setting reads as inert. There is no
    // toast in that case — `tabDenialReason` speaks only for permissions, and a
    // preference the user set themselves needs no explaining.
    //
    // The tab has TWO sources. Routes with a dedicated registration pass it as
    // `selectedTab`; the generic `/:tournamentId/:selectedTab` route — which is
    // how officials, matchUps, publishing and settings are all reached — leaves
    // it inside `data`. Reading only the parameter meant the guard silently did
    // nothing for exactly those tabs, and `...data` coming last then reinstated
    // the unguarded value even when it had fired. Hence both reads here, and
    // `selectedTab` assigned AFTER the spread so the guarded answer is final.
    let tab = selectedTab ?? data?.selectedTab;
    if (tab && !isTabAvailable(tab)) {
      const reason = tabDenialReason(tab);
      if (reason) tmxToast({ intent: 'is-warning', message: reason });
      tab = firstPermittedTab();
    }
    destroyTables();
    // ...data must come before the explicit keys, which are the decided values.
    displayTournament({ config: { renderDraw, renderPoints, ...data, selectedTab: tab } });
  };

  // Topology routes — standalone page, reuses tournament loading
  router.on(`/${TOURNAMENT}/:tournamentId/topology/:eventId/:drawId/${VIEW}`, (match) => {
    destroyTables();
    displayTournament({
      config: { ...match?.data, selectedTab: 'topology', readOnly: true },
    });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/topology/:eventId/:drawId`, (match) => {
    destroyTables();
    displayTournament({
      config: { ...match?.data, selectedTab: 'topology' },
    });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/topology/:eventId`, (match) => {
    destroyTables();
    displayTournament({
      config: { ...match?.data, selectedTab: 'topology' },
    });
  });

  router.on(`/${TOURNAMENT}/:tournamentId/format-wizard`, (match) => {
    displayTournament({
      config: { ...match?.data, selectedTab: 'format-wizard' },
    });
  });

  router.on(`/${TOURNAMENT}/:tournamentId`, (match) => {
    displayRoute({ data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId`, (match) => {
    displayRoute({ selectedTab: EVENTS_TAB, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId/draws`, (match) => {
    displayRoute({ selectedTab: EVENTS_TAB, renderDraw: true, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId/${DRAW}/:drawId`, (match) => {
    displayRoute({ selectedTab: EVENTS_TAB, renderDraw: true, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId/${DRAW}/:drawId/${VIEW}/:roundsView`, (match) => {
    displayRoute({ selectedTab: EVENTS_TAB, renderDraw: true, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId/${DRAW}/:drawId/${STRUCTURE}/:structureId`, (match) => {
    displayRoute({ selectedTab: EVENTS_TAB, renderDraw: true, data: match?.data });
  });
  router.on(
    `/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId/${DRAW}/:drawId/${STRUCTURE}/:structureId/${VIEW}/:roundsView`,
    (match) => {
      displayRoute({ selectedTab: EVENTS_TAB, renderDraw: true, data: match?.data });
    },
  );
  router.on(`/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId/points`, (match) => {
    displayRoute({ selectedTab: EVENTS_TAB, renderPoints: true, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${EVENT}/:eventId/${DRAW_ENTRIES}/:drawId`, (match) => {
    displayRoute({ selectedTab: EVENTS_TAB, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${PARTICIPANTS}/:participantView`, (match) => {
    displayRoute({ selectedTab: PARTICIPANTS, data: match?.data });
  });
  // Legacy bookmarks from before the workspace cutover. All scheduling URLs
  // now live under `/scheduling/:date/:mode` — `/schedule/:date` and
  // `/schedule2/:date(/:view)?` redirect; `/venues/availability` does too.
  router.on(`/${TOURNAMENT}/:tournamentId/schedule/:scheduledDate`, (match) => {
    const tournamentId = match?.data?.tournamentId;
    const scheduledDate = match?.data?.scheduledDate;
    if (tournamentId && scheduledDate) {
      router.navigate(`/${TOURNAMENT}/${tournamentId}/${SCHEDULING_TAB}/${scheduledDate}`);
    }
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${SCHEDULE2_TAB}/:scheduledDate/:scheduleView`, (match) => {
    const tournamentId = match?.data?.tournamentId;
    const scheduledDate = match?.data?.scheduledDate;
    const scheduleView = match?.data?.scheduleView;
    if (tournamentId && scheduledDate) {
      const mode = scheduleView === 'profile' ? 'profile' : 'grid';
      router.navigate(`/${TOURNAMENT}/${tournamentId}/${SCHEDULING_TAB}/${scheduledDate}/${mode}`);
    }
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${SCHEDULE2_TAB}/:scheduledDate`, (match) => {
    const tournamentId = match?.data?.tournamentId;
    const scheduledDate = match?.data?.scheduledDate;
    if (tournamentId && scheduledDate) {
      router.navigate(`/${TOURNAMENT}/${tournamentId}/${SCHEDULING_TAB}/${scheduledDate}`);
    }
  });
  // Bare `/schedule2` (no date) — the nav "Schedule" icon still targets this.
  // Formerly the retired renderSchedule2Tab resolved a date and bounced here;
  // now redirect straight to the workspace, which resolves its own date.
  // Must precede the generic `:selectedTab` catch-all below so it matches first.
  router.on(`/${TOURNAMENT}/:tournamentId/${SCHEDULE2_TAB}`, (match) => {
    const tournamentId = match?.data?.tournamentId;
    if (tournamentId) {
      router.navigate(`/${TOURNAMENT}/${tournamentId}/${SCHEDULING_TAB}`);
    }
  });
  // Unified scheduling workspace (Option C). Replaces /schedule2 and /venues/availability.
  router.on(`/${TOURNAMENT}/:tournamentId/${SCHEDULING_TAB}/:scheduledDate/:schedulingMode`, (match) => {
    displayRoute({ selectedTab: SCHEDULING_TAB, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${SCHEDULING_TAB}/:scheduledDate`, (match) => {
    displayRoute({ selectedTab: SCHEDULING_TAB, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${SCHEDULING_TAB}`, (match) => {
    displayRoute({ selectedTab: SCHEDULING_TAB, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${VENUES_TAB}/:venueView`, (match) => {
    // /venues/availability redirects into the scheduling workspace.
    const tournamentId = match?.data?.tournamentId;
    const venueView = match?.data?.venueView;
    if (venueView === 'availability' && tournamentId) {
      // 'today' sentinel — schedulingTab resolves it to the first valid
      // schedule date so the router stays engine-decoupled.
      router.navigate(`/${TOURNAMENT}/${tournamentId}/${SCHEDULING_TAB}/today/availability`);
      return;
    }
    displayRoute({ selectedTab: VENUES_TAB, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/${VENUE}/:venueId`, (match) => {
    displayRoute({ selectedTab: VENUES_TAB, data: match?.data });
  });
  // Deep link to a specific report (e.g. from the schedule page's timing-report
  // shortcut). More specific than the generic tab route below, so it wins.
  router.on(`/${TOURNAMENT}/:tournamentId/${REPORTS_TAB}/:reportId`, (match) => {
    displayRoute({ selectedTab: REPORTS_TAB, data: match?.data });
  });
  router.on(`/${TOURNAMENT}/:tournamentId/:selectedTab`, (match) => {
    displayRoute({ data: match?.data });
  });

  // adding a unique identifer to the URL will force refresh
  router.on(`/${TMX_TOURNAMENTS}/:uuid`, tmxTournaments);
  router.on(`/${TMX_TOURNAMENTS}`, tmxTournaments);

  router.on(`/${INVITE}/:inviteKey`, registrationModal);

  // SSO login: provisioner redirects user here with a one-time token
  router.on('/sso', async () => {
    const params = new URLSearchParams(globalThis.location?.hash?.split('?')[1] ?? '');
    const token = params.get('token');
    if (token) {
      try {
        const res = await ssoLoginWithToken(token);
        if (res?.status === 200 && res.data?.accessToken) {
          logIn({ data: { token: res.data.accessToken, refreshToken: res.data.refreshToken } });
        } else {
          logOut();
        }
      } catch {
        logOut();
      }
    } else {
      router.navigate(`/${TMX_TOURNAMENTS}`);
    }
  });

  // Magic-link login: the email lands the user on `#/magic/:code`. The code is
  // single-use and consumed server-side for an access + refresh session.
  router.on('/magic/:code', async (match) => {
    const code = match?.data?.code;
    if (!code) {
      router.navigate(`/${TMX_TOURNAMENTS}`);
      return;
    }
    try {
      const res = await consumeMagicLink(code);
      if (res?.status === 200 && res.data?.token) {
        logIn({ data: res.data });
      } else {
        tmxToast({ intent: 'is-danger', message: t('toasts.magicLinkInvalid') });
        router.navigate(`/${TMX_TOURNAMENTS}`);
      }
    } catch {
      tmxToast({ intent: 'is-danger', message: t('toasts.magicLinkInvalid') });
      router.navigate(`/${TMX_TOURNAMENTS}`);
    }
  });

  // A subject's SCHEDULE, which is not a calendar: the fixtures a programme took part in, read from
  // the participation index. The subject is in the path on purpose — it is what lets an operator
  // open a season for a subject that is not the provider they are currently impersonating.
  router.on(`/${PROVIDER}/:providerId/${SCHEDULE}`, (match) => {
    destroyTables();
    renderProviderSchedulePage(match?.data ?? undefined);
  });

  router.on(`/${TEMPLATES}/:templateView`, (match) => renderTemplatesPage(match?.data ?? undefined));
  router.on(`/${TEMPLATES}`, () => renderTemplatesPage());
  router.on(`/${POLICIES}/catalog`, renderPolicyCatalogPage);
  router.on(`/${POLICIES}`, renderPoliciesPage);
  router.on(`/${SETTINGS}`, renderSettingsPage);
  router.on('/admin', renderAdminPage);
  router.on(`/actionKey/:key`, (match) => {
    const key = match?.data?.key;
    if (key) queueKey(key);
    router.navigate(`/${TMX_TOURNAMENTS}`);
  });
  router.on('/', () => {
    // During initial load the splash animation is active — show it.
    // On any subsequent navigation to root, go straight to tournaments.
    const splash = document.getElementById(SPLASH);
    if (splash?.dataset.animating) {
      showSplash();
    } else {
      router.navigate(`/${TMX_TOURNAMENTS}`);
    }
  });
  router.notFound(() => {
    const splash = document.getElementById(SPLASH);
    if (splash?.dataset.animating) {
      showSplash();
    } else {
      router.navigate(`/${TMX_TOURNAMENTS}`);
    }
  });
  router.resolve();

  return { success: true };
}
